import { useEffect, useMemo, useState, type FormEvent } from "react";

import { createSchedule } from "../../api/programming.js";
import { Button } from "../../design-system/button.js";
import { Field, Select, Switch, TextInput } from "../../design-system/form.js";
import { Modal } from "../../design-system/modal.js";
import { Notice } from "../../design-system/surfaces.js";
import type { Schedule } from "../../api/types.js";

/**
 * A schedule and the timezone its hours are read in.
 *
 * The timezone belongs to the schedule rather than to a feed or to the browser: two
 * feeds on the same schedule change programme at the same moment wherever either of
 * them is being listened to, and the operator's own clock has no say in it. The list
 * is the platform's own zone database, so it is the same set the API validates
 * against.
 */
export function ScheduleDialog({
  onClose,
  onCreated,
  open,
}: {
  onClose: () => void;
  onCreated: (schedule: Schedule) => void;
  open: boolean;
}) {
  const zones = useMemo(readTimeZones, []);
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState(browserTimeZone());
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setTimezone(browserTimeZone());
    setEnabled(true);
    setError(null);
  }, [open]);

  const ready = name.trim() !== "" && timezone.trim() !== "" && !busy;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    createSchedule({ name: name.trim(), timezone: timezone.trim(), enabled })
      .then((created) => {
        if (created) onCreated(created);
        onClose();
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? cause.message
            : "The schedule was not saved.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }

  return (
    <Modal
      description="Which playlist plays when."
      onClose={onClose}
      open={open}
      title="New schedule"
    >
      <form className="flex flex-col gap-5" noValidate onSubmit={submit}>
        <Field htmlFor="schedule-name" label="Name">
          <TextInput
            autoFocus
            id="schedule-name"
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder="Weekday programming"
            required
            value={name}
          />
        </Field>

        <Field
          hint="All times in this schedule use this timezone."
          htmlFor="schedule-timezone"
          label="Timezone"
        >
          {zones.length > 0 ? (
            <Select
              id="schedule-timezone"
              onChange={(event) => {
                setTimezone(event.target.value);
              }}
              value={timezone}
            >
              {zones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </Select>
          ) : (
            <TextInput
              id="schedule-timezone"
              onChange={(event) => {
                setTimezone(event.target.value);
              }}
              placeholder="Europe/London"
              required
              value={timezone}
            />
          )}
        </Field>

        <Switch
          checked={enabled}
          description="A feed can only be assigned an enabled schedule."
          label="Enabled"
          onChange={setEnabled}
        />

        {error ? <Notice tone="danger">{error}</Notice> : null}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={!ready} type="submit">
            {busy ? "Creating…" : "Create schedule"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function readTimeZones(): string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supported !== "function") return [];
  try {
    const zones = supported("timeZone");
    return zones.includes("UTC") ? zones : ["UTC", ...zones];
  } catch {
    return [];
  }
}
